import React, { FC, useEffect } from "react";

import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";

import { FieldError, InputLabel, SelectInput, SwitchInput } from "@/components/atoms";
import useContractQueries from "@/components/features/Contracts/useContractQueries";
import { CustomerCardFormType } from "@/components/features/ToDos/CustomerCardPage/formType";
import { StyledCustomerTypography } from "@/components/features/ToDos/CustomerCardPage/styled";
import { FormPanel, StepComponentProps } from "@/components/organisms";
import { policyholderApi } from "@/shared/apis";

import { ContractType, customerCardAuthorizationDefaultValues } from "./formType";

const AuthorizationStep: FC<StepComponentProps> = () => {
    const { t } = useTranslation();
    const {
        formState: { errors },
        control,
        setValue,
        watch,
        getValues,
    } = useFormContext<CustomerCardFormType>();

    const giveAuthorizationPermission = watch("authorization.giveAuthorizationPermission");
    const contractType = watch("authorization.contractType");
    const selectedContracts = watch("authorization.selectedContracts");
    const selectedMandate = watch("authorization.selectedMandate");

    const ibpAclId = getValues("accessRightsId");
    const policyholderId = getValues("policyholderId");

    const { data: mandates = [] } = useQuery(
        ["policyholderMandates", { policyholderId }],
        () => policyholderApi.getPolicyholderMandates(+policyholderId, ibpAclId),
        { enabled: !!policyholderId },
    );
    const mandateRelationSelected = mandates.find((mandate) => `${mandate.internalId}` === `${selectedMandate}`);

    const { useContractsSearchQuery } = useContractQueries(policyholderId);
    const { result: contractsQuery } = useContractsSearchQuery();

    const contracts = contractsQuery.unique("title");

    useEffect(() => {
        if (!giveAuthorizationPermission) {
            setValue("authorization", customerCardAuthorizationDefaultValues());
        }
    }, [giveAuthorizationPermission]);

    useEffect(() => {
        if (contractType === ContractType.ALL) {
            setValue("authorization.selectedContracts", []);
            setValue("authorization.forAllContracts", true);
            setValue("authorization.forSpecificContracts", false);
        } else if (contractType === ContractType.SELECTED) {
            setValue("authorization.forAllContracts", false);
            setValue("authorization.forSpecificContracts", true);
        }
    }, [contractType]);

    useEffect(() => {
        if (selectedMandate) {
            setValue("authorization.mandates", [mandateRelationSelected]);
        }
    }, [selectedMandate]);

    useEffect(() => {
        if (contractsQuery.length) {
            setValue(
                "authorization.contracts",
                selectedContracts.map((contract) => contracts.find((e) => e.id === contract)?.title).join(","),
            );
        }
    }, [selectedContracts, contractsQuery]);

    return (
        <>
            <FormPanel title={t("CustomerCard.General.ConsultAuthorization")} mode="edit">
                <SwitchInput
                    name="authorization.giveAuthorizationPermission"
                    control={control}
                    fullWidth={false}
                    label={t("CustomerCard.UsagePreference.GiveAuthorizationPermission")}
                />
                {giveAuthorizationPermission && (
                    <>
                        <SelectInput
                            name="authorization.selectedMandate"
                            control={control}
                            FormControlProps={{ style: { maxWidth: "550px" } }}
                            label={t("CustomerCard.ConsultAuthorization.PolicyHolderAuthorization")}
                            required
                            options={mandates.map((mandate) => ({
                                id: mandate.internalId,
                                name: [mandate.fullName, mandate.relationType.name].filter(Boolean).join(" - "),
                            }))}
                        />
                        <FormControl error={Boolean(errors.authorization?.contractType)} className="mb10">
                            <InputLabel
                                required
                                htmlFor="authorization.contractType"
                                text={t("CustomerCard.ConsultAuthorization.ContractsPermissionType")}
                            />
                            <Controller
                                name="authorization.contractType"
                                control={control}
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <FormControlLabel
                                            value={ContractType.ALL}
                                            label={t("CustomerCard.ConsultAuthorization.AllContracts")}
                                            control={
                                                <Radio
                                                    data-testid="radio-allContracts"
                                                    inputProps={{
                                                        // Necessary, waiting https://github.com/mui/material-ui/issues/34154 than remove
                                                        // @ts-ignore
                                                        "data-testid": `radio-input-allContracts`,
                                                    }}
                                                />
                                            }
                                        />
                                        <FormControlLabel
                                            value={ContractType.SELECTED}
                                            label={t("CustomerCard.ConsultAuthorization.SelectedContracts")}
                                            control={
                                                <Radio
                                                    data-testid="radio-selectContracts"
                                                    inputProps={{
                                                        // Necessary, waiting https://github.com/mui/material-ui/issues/34154 than remove
                                                        // @ts-ignore
                                                        "data-testid": `radio-input-selectContracts`,
                                                    }}
                                                />
                                            }
                                        />
                                    </RadioGroup>
                                )}
                            />
                            <FieldError error={errors.authorization?.contractType} />
                        </FormControl>
                        {contractType === ContractType.SELECTED && (
                            <SelectInput
                                name="authorization.selectedContracts"
                                control={control}
                                multiple
                                options={contracts.map((contract) => ({
                                    id: contract.id,
                                    name: `${contract.domain.name} (Ref ${contract.reference})`,
                                }))}
                                FormControlProps={{ style: { maxWidth: "550px" } }}
                            />
                        )}
                        <StyledCustomerTypography>
                            {t("CustomerCard.ConsultAuthorization.AccessToFile")}{" "}
                            <span data-testid="relationEmail">{mandateRelationSelected?.email || "-"}</span>
                        </StyledCustomerTypography>
                    </>
                )}
            </FormPanel>
        </>
    );
};

export default AuthorizationStep;
